import { atom } from 'jotai';

import { loadUpdateAtom } from '../_graphql/mutationLoadUpdate';

import { createAutoSaveFormAtoms } from './createAutoSaveFormAtoms.state';
import { $outboundLoads, $outboundTicketDetail } from './scaleTicketPro.state';

export type OutboundCustomerFormState = {
  facilityId: string | null;
  customerAccountId: string | null;
  customerAccountAddressId: string | null;
  salesOrderId: string | null;
  receivingFacilityId: string | null;
  carrierAccountId: string | null;
  freightTerms: string | null;
  shippingCarrierName: string | null;
  scac: string | null;
};

export const DEFAULT_OUTBOUND_CUSTOMER_FORM: OutboundCustomerFormState = {
  facilityId: null,
  customerAccountId: null,
  customerAccountAddressId: null,
  salesOrderId: null,
  receivingFacilityId: null,
  carrierAccountId: null,
  freightTerms: null,
  shippingCarrierName: null,
  scac: null,
};

type OutboundCustomerServerData = {
  facilityId: string | null;
  customerAccountId: string | null;
  customerAccountAddressId: string | null;
  salesOrderId: string | null;
  receivingFacilityId: string | null;
  carrierAccountId: string | null;
  freightTerms: string | null;
  shippingCarrierName: string | null;
  scac: string | null;
};

const atoms = createAutoSaveFormAtoms<
  OutboundCustomerFormState,
  OutboundCustomerServerData
>({
  defaultForm: DEFAULT_OUTBOUND_CUSTOMER_FORM,
  fromServerData: (load) => ({
    facilityId: load.facilityId,
    customerAccountId: load.customerAccountId,
    customerAccountAddressId: load.customerAccountAddressId,
    salesOrderId: load.salesOrderId,
    receivingFacilityId: load.receivingFacilityId,
    carrierAccountId: load.carrierAccountId,
    freightTerms: load.freightTerms,
    shippingCarrierName: load.shippingCarrierName,
    scac: load.scac,
  }),
  createMutation: (onSuccess, onError) => {
    const $mut = loadUpdateAtom({
      onSuccess: (get, set, { params }) => onSuccess(get, set, params.id),
      onError: (get, set, { params }) => onError(get, set, params.id),
    });
    return atom(
      null,
      (
        _get,
        set,
        params: { ticketId: string; form: OutboundCustomerFormState },
      ) => {
        set($mut, {
          id: params.ticketId,
          facilityId: params.form.facilityId ?? undefined,
          customerAccountId: params.form.customerAccountId,
          customerAccountAddressId: params.form.customerAccountAddressId,
          carrierAccountId: params.form.carrierAccountId,
          receivingFacilityId: params.form.receivingFacilityId,
          salesOrderId: params.form.salesOrderId,
          freightTerms: params.form.freightTerms ?? undefined,
          shippingCarrierName: params.form.shippingCarrierName ?? undefined,
          scac: params.form.scac ?? undefined,
        });
      },
    );
  },
  refetchOnSuccess: (get) => {
    get($outboundTicketDetail).refetch();
    get($outboundLoads).refetch();
  },
});

export const $outboundCustomerFormById = atoms.$formById;
export const $outboundCustomerBaselineById = atoms.$baselineById;
export const $updateOutboundCustomerTextField = atoms.$updateField;
export const $syncOutboundCustomerForm = atoms.$syncForm;
export const $flushOutboundCustomerAutoSave = atoms.$flushAutoSave;
export const $cancelOutboundCustomerAutoSave = atoms.$cancelAutoSave;

export const $cleanupOutboundCustomerForm = atoms.$cleanupForm;

export const $updateOutboundCustomerSelectField = atom(
  null,
  (
    _get,
    set,
    params: {
      field: keyof OutboundCustomerFormState;
      value: string | null;
      ticketId: string;
    },
  ) => {
    set(atoms.$updateImmediately, {
      ticketId: params.ticketId,
      updates: {
        [params.field]: params.value,
      } as Partial<OutboundCustomerFormState>,
    });
  },
);

export const $updateOutboundCustomerSelectFields = atom(
  null,
  (
    _get,
    set,
    params: {
      updates: Partial<OutboundCustomerFormState>;
      ticketId: string;
    },
  ) => {
    set(atoms.$updateImmediately, {
      ticketId: params.ticketId,
      updates: params.updates,
    });
  },
);
